import type { User } from "firebase/auth";
import type { UserProfile, UserProfileUpdate } from "@/types/user";
import type { UsageLogPayload } from "@/types/usage";

export class ProfileRequestError extends Error {
  status: number;
  body: unknown;

  constructor({ message, status, body }: { message: string; status: number; body: unknown }) {
    super(message);
    this.name = "ProfileRequestError";
    this.status = status;
    this.body = body;
  }
}

async function buildAuthHeaders(
  user: User,
  options: { includeJson?: boolean } = {}
): Promise<HeadersInit> {
  const { includeJson = false } = options;
  const headers: Record<string, string> = {
    "X-Sc-Auth-Sub": user.uid,
    "X-Sc-Auth-Provider": user.providerData[0]?.providerId ?? user.providerId ?? "firebase",
  };

  if (user.email) {
    headers["X-Sc-Auth-Email"] = user.email;
  }

  if (user.displayName) {
    headers["X-Sc-Auth-Name"] = user.displayName;
  }

  if (user.photoURL) {
    headers["X-Sc-Auth-Avatar"] = user.photoURL;
  }

  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }

  try {
    const token = await user.getIdToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  } catch (error) {
    console.warn("Unable to retrieve ID token for profile request", error);
  }

  return headers;
}

async function requestProfile(
  user: User,
  input: RequestInfo | URL,
  init?: (RequestInit & { asJson?: boolean }) | undefined
): Promise<UserProfile> {
  const { asJson, ...requestInit } = init ?? {};
  const includeJson = asJson ?? (requestInit.body !== undefined && !(requestInit.body instanceof FormData));
  const authHeaders = await buildAuthHeaders(user, { includeJson });
  const mergedHeaders = new Headers(init?.headers ?? {});
  Object.entries(authHeaders).forEach(([key, value]) => {
    mergedHeaders.set(key, value);
  });

  const response = await fetch(input, {
    credentials: "include",
    ...requestInit,
    headers: mergedHeaders,
  });

  const responseText = await response.text();
  let parsedBody: unknown = null;
  let parseError: unknown = null;

  if (responseText) {
    try {
      parsedBody = JSON.parse(responseText);
    } catch (error) {
      parseError = error;
    }
  }

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;

    if (typeof parsedBody === "object" && parsedBody !== null) {
      const maybeDetail = (parsedBody as Record<string, unknown>).detail;
      const maybeError = (parsedBody as Record<string, unknown>).error;
      const candidate = typeof maybeDetail === "string" ? maybeDetail : typeof maybeError === "string" ? maybeError : null;
      if (candidate) {
        message = candidate;
      }
    } else if (typeof responseText === "string" && responseText.trim().length > 0) {
      message = responseText;
    }

    throw new ProfileRequestError({
      message,
      status: response.status,
      body: parsedBody ?? responseText,
    });
  }

  if (!responseText) {
    throw new ProfileRequestError({
      message: "Profile response was empty.",
      status: response.status,
      body: null,
    });
  }

  if (parseError) {
    throw new ProfileRequestError({
      message: "Unable to parse profile response.",
      status: response.status,
      body: responseText,
    });
  }

  return parsedBody as UserProfile;
}

const USER_API_BASE = "/pyapi/user/me" as const;

export async function fetchCurrentUserProfile(user: User): Promise<UserProfile> {
  // Add timestamp to bypass Firebase Hosting cache
  const url = `${USER_API_BASE}?_t=${Date.now()}`;
  return requestProfile(user, url, { method: "GET" });
}

export async function patchCurrentUserProfile(user: User, update: UserProfileUpdate): Promise<UserProfile> {
  return requestProfile(user, USER_API_BASE, {
    method: "PATCH",
    body: JSON.stringify(update),
    asJson: true,
  });
}

export async function replaceUserSettings(
  user: User,
  settings: Record<string, unknown>
): Promise<UserProfile> {
  return requestProfile(user, `${USER_API_BASE}/settings`, {
    method: "PUT",
    body: JSON.stringify({ settings }),
    asJson: true,
  });
}

export async function uploadUserAvatar(user: User, file: File): Promise<UserProfile> {
  const formData = new FormData();
  formData.append("avatar", file);

  return requestProfile(user, `${USER_API_BASE}/avatar`, {
    method: "POST",
    body: formData,
    asJson: false,
  });
}

export async function deleteUserAvatar(user: User): Promise<UserProfile> {
  return requestProfile(user, `${USER_API_BASE}/avatar`, {
    method: "DELETE",
  });
}

export async function logUserUsage(user: User, usage: UsageLogPayload): Promise<UserProfile> {
  const metrics: Record<string, number> = {};

  Object.entries(usage.metrics ?? {}).forEach(([key, value]) => {
    const normalizedKey = String(key || "").trim();
    if (!normalizedKey) {
      return;
    }
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue) || numericValue < 0) {
      return;
    }
    metrics[normalizedKey] = numericValue;
  });

  const payload = {
    provider: usage.provider,
    model: usage.model ?? null,
    metrics,
    requestType: usage.requestType ?? null,
  };

  return requestProfile(user, `${USER_API_BASE}/usage`, {
    method: "POST",
    body: JSON.stringify(payload),
    asJson: true,
  });
}

export async function updateLastActive(user: User): Promise<UserProfile> {
  return requestProfile(user, `${USER_API_BASE}/last-active`, {
    method: "PATCH",
    asJson: true,
  });
}

export async function deleteUserAccount(user: User): Promise<void> {
  const headers = await buildAuthHeaders(user);
  const response = await fetch(USER_API_BASE, {
    method: "DELETE",
    credentials: "include",
    headers,
  });

  if (response.ok) {
    return;
  }

  const responseText = await response.text();
  let message = responseText || `Request failed with status ${response.status}`;

  if (responseText) {
    try {
      const parsed = JSON.parse(responseText) as { detail?: unknown };
      if (typeof parsed?.detail === "string") {
        message = parsed.detail;
      }
    } catch {
      /* ignore parse errors */
    }
  }

  throw new Error(message);
}
